import React, { useEffect, useState } from 'react';
import ForensicVerdictBanner from './ForensicVerdictBanner';
import ForensicSynthesisBanner from './ForensicSynthesisBanner';
import ForensicEvidenceCard from './ForensicEvidenceCard';
import ForensicHeatmap from './ForensicHeatmap';
import BeneishBreakdown from './BeneishBreakdown';
import ForensicAgentModal from './ForensicAgentModal';
import '../styles/ForensicTab.css';

const EVIDENCE_GROUPS = [
    {
        id: 'manipulation',
        title: 'Manipulation-Risk Signals',
        names: ['beneishMScore', 'montierCScore', 'sloanAccrualRatio'],
    },
    {
        id: 'strength',
        title: 'Financial-Strength Signal',
        names: ['piotroskiFScore'],
    },
    {
        id: 'solvency',
        title: 'Solvency Signal',
        names: ['altmanZScore'],
    },
];

const HIDDEN_FALLBACK = ['cfoToNetProfit', 'qualityOfEarnings'];

const groupOf = (card) => {
    if (card?.group) return card.group;
    return EVIDENCE_GROUPS.find((group) => group.names.includes(card?.name))?.id || 'manipulation';
};

const agentExplanation = (analysis, agent, cards = []) => {
    if (!agent || !analysis) return null;
    if (agent.metric) {
        const sliced = analysis.per_metric_explanations?.[agent.metric];
        const card = cards.find((row) => row.name === agent.metric);
        if (!sliced && !card) return null;
        return {
            ...(sliced || {}),
            confidence: analysis.confidence,
            top_reasons: analysis.top_reasons,
            plain_language:
                sliced?.plain_language
                || sliced?.plainLanguage
                || card?.plainLanguage
                || card?.plain_language,
        };
    }
    return {
        verdict: analysis.verdict,
        why: analysis.aggregate_verdict || analysis.summary,
        plain_language: analysis.plain_language || analysis.plainLanguage,
        confidence: analysis.confidence,
        top_reasons: analysis.top_reasons,
        per_metric_explanations: analysis.per_metric_explanations,
    };
};

export default function ForensicTab({
    report = null,
    ratios = [],
    financials,
    companyName,
    companyId,
    basis = 'consolidated',
    apiUrl,
}) {
    const [beneishCard, setBeneishCard] = useState(null);
    const [agent, setAgent] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const verdicts = report?.verdicts || {};
    const tierB = Array.isArray(report?.tierB) ? report.tierB.slice(0, 8) : [];
    const tierC = Array.isArray(report?.tierC) ? report.tierC : [];
    const heatmapRows = tierC.length
        ? tierC
        : (ratios || []).filter((row) => !row.hidden && !HIDDEN_FALLBACK.includes(row.name));
    const canAskAgent = Boolean(companyId && apiUrl);

    useEffect(() => {
        if (!companyId || !apiUrl) return undefined;
        const controller = new AbortController();
        setAnalysisLoading(true);
        setAnalysisError(null);
        const params = new URLSearchParams({ basis: basis || 'consolidated' });
        const url = `${apiUrl}/finlytics/forensic/${encodeURIComponent(companyId)}/verdict?${params.toString()}`;
        fetch(url, { signal: controller.signal })
            .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
                if (!ok || !body?.success) {
                    throw new Error(body?.message || 'Could not load forensic agent.');
                }
                setAnalysis(body.data || null);
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                setAnalysisError(err.message || 'Could not load forensic agent.');
            })
            .finally(() => setAnalysisLoading(false));
        return () => controller.abort();
    }, [companyId, basis, apiUrl]);

    if (!report && !(ratios || []).length) {
        return <p className="forensic-empty">No forensic ratios available.</p>;
    }

    return (
        <div className="forensic-tab">
            {canAskAgent && (
                <ForensicSynthesisBanner
                    analysis={analysis}
                    loading={analysisLoading}
                    error={analysisError}
                />
            )}
            <div className="forensic-tier-a">
                <ForensicVerdictBanner
                    axis="manipulation"
                    verdict={verdicts.manipulation}
                    onAskAgent={canAskAgent ? setAgent : undefined}
                />
                <ForensicVerdictBanner axis="solvency" verdict={verdicts.solvency} />
            </div>

            {Array.isArray(report?.legend) && report.legend.length > 0 && (
                <aside className="forensic-legend" aria-label="Severity thresholds">
                    <h3 className="forensic-legend__title">Disclosed cutoffs</h3>
                    <ul className="forensic-legend__list">
                        {report.legend.map((item) => (
                            <li key={item.metric}>
                                <strong>{item.metric}</strong>
                                <span>{item.zones}</span>
                                <em>{item.source}</em>
                            </li>
                        ))}
                    </ul>
                </aside>
            )}

            {tierB.length > 0 && (
                <section className="forensic-tier-b">
                    {EVIDENCE_GROUPS.map((group) => {
                        const cards = tierB.filter((card) => groupOf(card) === group.id);
                        if (!cards.length) return null;
                        return (
                            <div key={group.id} className="forensic-evidence-group">
                                <h3 className="forensic-section-title">{group.title}</h3>
                                <div className="forensic-card-grid">
                                    {cards.map((card) => (
                                        <ForensicEvidenceCard
                                            key={card.name}
                                            card={card}
                                            onBeneishOpen={setBeneishCard}
                                            onAskAgent={canAskAgent ? setAgent : undefined}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            <section className="forensic-tier-c">
                <ForensicHeatmap
                    rows={heatmapRows}
                    financials={financials}
                    companyName={companyName}
                    defaultOpen={false}
                />
            </section>

            {beneishCard && (
                <BeneishBreakdown
                    year={beneishCard.year}
                    mScore={beneishCard.value}
                    zone={beneishCard.zone}
                    components={beneishCard.components}
                    topDrivers={beneishCard.topDrivers}
                    scoreExplain={beneishCard.scoreExplain}
                    onClose={() => setBeneishCard(null)}
                />
            )}
            <ForensicAgentModal
                open={Boolean(agent)}
                title={agent?.title}
                companyId={companyId}
                basis={basis}
                metric={agent?.metric}
                apiUrl={apiUrl}
                card={agent?.metric ? tierB.find((row) => row.name === agent.metric) : null}
                explanation={agentExplanation(analysis, agent, tierB)}
                onClose={() => setAgent(null)}
            />
        </div>
    );
}
